// =============================================================================
// SIREPPAR - Página de Listado de Partidos
// =============================================================================

import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, FunnelIcon, DocumentArrowDownIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import DataTable, { Column } from '../../components/common/DataTable';
import { partidoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ETAPAS_RECONOCIMIENTO, etapaIndexDeFase } from '../../utils/fasesReconocimiento';
import { coincideBusqueda } from '../../utils/busqueda';

// =============================================================================
// Tipos
// =============================================================================

interface Partido {
  id: string;
  numero: number;
  denominacion: string;
  sigla: string;
  tipo: string;
  estado: string;
  fechaReconocimiento?: string;
  _count?: {
    afiliaciones: number;
  };
}

interface DetalleMandato {
  organo: string;
  fechaHasta: string | null;
  estado: 'VENCIDO' | 'POR_VENCER';
  autoridades: { nombre: string; apellido: string }[];
}

interface PartidoCumplimiento {
  id: string;
  numero: number;
  denominacion: string;
  sigla: string;
  tipo: string;
  // Qué es la fila: partido común, confederación o alianza (`Partido.clase`).
  clase: string;
  estado: string;
  localidad: string | null;
  // Nota interna libre de la Secretaría sobre el partido.
  nota: string | null;
  afiliadosValidos: number;
  minimoRequerido: number;
  // null = no le corresponde (solo un partido común tiene padrón propio).
  cumple4PorMil: boolean | null;
  // Trámite en curso del partido (reconocimiento o reempadronamiento). Da la etapa
  // real, que partido.estado no distingue. null si el partido no tiene expediente.
  expediente: {
    id: string;
    numero: string;
    tipo: string;
    estado: string;
    fase: string | null;
  } | null;
  paridad: 'CUMPLE' | 'NO_CUMPLE' | 'SIN_DATOS';
  ultimaActualizacion: string | null;
  estadoMandatos: 'VERDE' | 'AMARILLO' | 'ROJO' | 'SIN_DATOS';
  detalleMandatos: DetalleMandato[];
  participacionElectoral: null;
}

// Etiquetas y colores del estado del EXPEDIENTE (distinto del estado del partido).
// Mismos textos que /gestion y el detalle del expediente.
const ESTADO_EXPEDIENTE_LABELS: Record<string, string> = {
  BORRADOR: 'Borrador',
  PRESENTADO: 'Presentado',
  EN_ADMISIBILIDAD: 'En Admisibilidad',
  INTIMADO: 'Intimado',
  EN_ANALISIS: 'En Análisis',
  OBSERVADO: 'Observado',
  NO_PRESENTADO: 'No Presentado',
  EN_SECRETARIO: 'En Secretario',
  PENDIENTE_RESOLUCION: 'En Secretario',
  EN_TEP: 'En el TEP',
  REC_PROVISORIO: 'Reconocimiento provisorio',
  VIGENTE: 'Vigente',
  APROBADO: 'Aprobado',
  RECHAZADO: 'Rechazado',
  CADUCO: 'Caduco',
  EXTINGUIDO: 'Extinguido',
  ARCHIVADO: 'Archivado',
};


// Cómo se rotula cada tipo de expediente en la columna "Exp. Inicial".
const TRAMITE_LABELS: Record<string, string> = {
  RECONOCIMIENTO: 'Reconocimiento',
  REEMPADRONAMIENTO: 'Reempadronamiento',
  CONSTITUCION_ALIANZA: 'Constitución de alianza',
  MODIFICACION_ALIANZA: 'Modificación de alianza',
};

// =============================================================================
// Constantes
// =============================================================================

const estadoColors: Record<string, string> = {
  // BORRADOR: trámite sin presentar (wizard de alianza a medio cargar). Distinto de
  // EN_FORMACION, que ya está presentado y en trámite.
  BORRADOR: 'bg-gray-100 text-gray-700',
  EN_FORMACION: 'bg-yellow-100 text-yellow-800',
  PENDIENTE_REVISION: 'bg-blue-100 text-blue-800',
  RECONOCIDO: 'bg-blue-100 text-blue-800',
  VIGENTE: 'bg-green-100 text-green-800',
  SUSPENDIDO: 'bg-orange-100 text-orange-800',
  CADUCO: 'bg-red-100 text-red-800',
  EXTINGUIDO: 'bg-gray-100 text-gray-800',
  FUSIONADO: 'bg-purple-100 text-purple-800',
  ABSORBIDO: 'bg-purple-100 text-purple-800',
};

const estadoLabels: Record<string, string> = {
  BORRADOR: 'Borrador',
  EN_FORMACION: 'En Formación',
  RECONOCIDO: 'Reconocido',
  VIGENTE: 'Vigente',
  SUSPENDIDO: 'Suspendido',
  CADUCO: 'Caduco',
  EXTINGUIDO: 'Extinguido',
  FUSIONADO: 'Fusionado',
  ABSORBIDO: 'Absorbido',
};

// Ámbito territorial. CONFEDERACION dejó de ser un `TipoPartido` y pasó a ser un valor
// de `Partido.clase`, que es ORTOGONAL al ámbito: una confederación provincial tiene
// tipo PROVINCIAL y clase CONFEDERACION.
const tipoLabels: Record<string, string> = {
  MUNICIPAL: 'Municipal',
  DISTRITO: 'Distrital',
  PROVINCIAL: 'Provincial',
  NACIONAL: 'Nacional',
};

// Clase de agrupación (`ClaseAgrupacion`). Este listado muestra las tres: un partido
// común no se rotula (es el caso normal), una confederación y una alianza sí.
const claseLabels: Record<string, string> = {
  ESTANDAR: 'Partido',
  CONFEDERACION: 'Confederación',
  ALIANZA: 'Alianza',
};

const claseBadgeColors: Record<string, string> = {
  CONFEDERACION: 'bg-teal-100 text-teal-700',
  ALIANZA: 'bg-indigo-100 text-indigo-700',
};

// =============================================================================
// Celda "Nota" — texto libre por partido
// =============================================================================
// Se persiste en `Partido.nota` (columna `text`; antes se llamaba
// `denominacion_completa`, un campo que nunca se usó). El backend la acota a
// 2000 caracteres.
//
// El botón "Anotar" va DENTRO del textarea, pegado a la esquina inferior
// derecha, para no robarle ancho a la nota — que es la columna más ancha de la
// tabla justamente porque es la que más se lee.

function CeldaNota({ partido, puedeAnotar }: { partido: PartidoCumplimiento; puedeAnotar: boolean }) {
  const queryClient = useQueryClient();
  const [texto, setTexto] = useState(partido.nota ?? '');
  const [guardando, setGuardando] = useState(false);

  // Si la nota cambia por afuera (refetch tras guardar otra fila), se re-sincroniza.
  useEffect(() => { setTexto(partido.nota ?? ''); }, [partido.nota]);

  const sucio = texto.trim() !== (partido.nota ?? '').trim();

  const anotar = async () => {
    if (!sucio || guardando) return;
    setGuardando(true);
    try {
      await partidoService.updateNota(partido.id, texto.trim() || null);
      // Actualización local: refetchear todo el cumplimiento por una nota sería
      // pedir de nuevo los conteos de afiliados de los 39 partidos.
      queryClient.setQueryData(
        ['partidos-cumplimiento', 'con-inactivos'],
        (prev: PartidoCumplimiento[] | undefined) =>
          prev?.map(x => (x.id === partido.id ? { ...x, nota: texto.trim() || null } : x)),
      );
      // La MISMA nota se ve en el tablero de documentación del expediente
      // (expediente.partido.nota). Con staleTime de 5 minutos, sin invalidar acá el
      // expediente seguía mostrando la nota anterior al entrar desde este listado.
      queryClient.invalidateQueries({ queryKey: ['expediente'] });
      toast.success('Nota guardada');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo guardar la nota');
    } finally {
      setGuardando(false);
    }
  };

  if (!puedeAnotar) {
    return (
      <td className="px-1.5 py-2 align-top">
        {partido.nota
          ? (
            // Mismo cuadro que la nota del tablero de documentación
            // (components/common/CuadroNota): es la MISMA nota del partido, así que
            // tiene que verse igual en las dos pantallas.
            <p className="bg-yellow-50 border border-gray-400 rounded-md px-2.5 py-1.5 text-xs text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
              {partido.nota}
            </p>
          )
          : <span className="text-xs text-gray-300">—</span>}
      </td>
    );
  }

  return (
    <td className="px-1.5 py-2 align-top">
      <div className="relative min-w-0">
        <textarea
          value={texto}
          onChange={e => setTexto(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder="Escribí una nota…"
          className="block w-full max-w-full min-w-0 resize-y bg-yellow-50 border border-gray-300 rounded-md pl-2 pr-2 pt-1.5 pb-7 text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
        <button
          onClick={anotar}
          disabled={!sucio || guardando}
          title={sucio ? 'Guardar la nota' : 'No hay cambios para guardar'}
          className="absolute bottom-1 right-1 border border-black text-black bg-white text-[11px] leading-none px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {guardando ? 'Guardando…' : 'Anotar'}
        </button>
      </div>
    </td>
  );
}

// =============================================================================
// Componente
// =============================================================================

export default function PartidosListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE'].includes(user?.rol || '');
  const isCumplimiento = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ANALISTA', 'AUDITOR'].includes(user?.rol || '');

  // Estado de filtros y paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [estadoFilter, setEstadoFilter] = useState<string>('');
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const [claseFilter, setClaseFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(true);
  const [soloAtencion, setSoloAtencion] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exportandoId, setExportandoId] = useState<string | null>(null);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  // Orden por número de partido. Apagado = orden que manda el backend (por tipo y
  // después por denominación). Encendido = numérico ascendente cruzando los tipos.
  const [ordenPorNro, setOrdenPorNro] = useState(false);
  // Quién puede escribir la nota. Espeja Roles.INTERNOS_ESCRITURA del backend
  // (MESA_ENTRADAS y AUDITOR quedan afuera: solo lectura).
  const puedeAnotar = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ANALISTA'].includes(user?.rol || '');

  const handleExportarHoja = async (p: PartidoCumplimiento) => {
    if (exportandoId) return;
    setExportandoId(p.id);
    try {
      await partidoService.descargarHojaInspeccion(p.id, p.sigla);
      toast.success('Hoja de inspección descargada');
    } catch (err: any) {
      let mensaje = 'No se pudo generar la hoja de inspección';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          mensaje = parsed?.error?.message || parsed?.message || mensaje;
        } catch { /* respuesta no-JSON */ }
      }
      toast.error(mensaje);
    } finally {
      setExportandoId(null);
    }
  };

  // Query para obtener partidos (vista estándar)
  const { data, isLoading, error } = useQuery({
    queryKey: ['partidos', page, pageSize, search, estadoFilter, tipoFilter],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page,
        pageSize,
      };
      if (search) params.search = search;
      if (estadoFilter) params.estado = estadoFilter;
      if (tipoFilter) params.tipo = tipoFilter;

      const response = await partidoService.list(params);
      return response.data;
    },
    enabled: !isCumplimiento,
  });

  // Query de cumplimiento (solo para roles analíticos)
  const { data: cumplimientoRaw, isLoading: cumplimientoLoading } = useQuery({
    // Se piden CON inactivos: esta pantalla es el listado de partidos, así que un
    // partido caduco o extinguido tiene que aparecer. Sin esto quedaban invisibles
    // (la consulta de cumplimiento los excluye para no ensuciar las métricas).
    queryKey: ['partidos-cumplimiento', 'con-inactivos'],
    queryFn: async () => {
      const r = await partidoService.getCumplimiento(true);
      return r.data.data as PartidoCumplimiento[];
    },
    enabled: isCumplimiento,
  });

  // Etapa del trámite en curso. En RECONOCIMIENTO la etapa se deriva de la FASE (el
  // estado es solo un overlay); en REEMPADRONAMIENTO y el resto, del estado.
  const etapaTramite = (p: PartidoCumplimiento): { tramite: string; etapa: string } | null => {
    const e = p.expediente;
    if (!e) return null;
    if (e.tipo === 'RECONOCIMIENTO') {
      const etapa = e.fase
        ? ETAPAS_RECONOCIMIENTO[etapaIndexDeFase(e.fase as any)] ?? 'En trámite'
        : 'En trámite';
      return { tramite: 'Reconocimiento', etapa };
    }
    // Fuera del reconocimiento la etapa se lee del estado. El trámite se rotula por
    // tipo: dar "Reempadronamiento" por descarte mentía en cuanto entró la alianza.
    return {
      tramite: TRAMITE_LABELS[e.tipo] ?? e.tipo,
      etapa: ESTADO_EXPEDIENTE_LABELS[e.estado] ?? e.estado,
    };
  };

  // Los partidos que ya no están en actividad no tienen obligaciones que cumplir:
  // marcarlos en rojo llenaría de ruido el filtro "solo atención".
  const ESTADOS_INACTIVOS = ['CADUCO', 'EXTINGUIDO', 'FUSIONADO', 'ABSORBIDO'];
  const esInactivo = (p: PartidoCumplimiento) => ESTADOS_INACTIVOS.includes(p.estado);

  const calcularEstadoGeneral = (p: PartidoCumplimiento): 'VERDE' | 'AMARILLO' | 'ROJO' => {
    if (esInactivo(p)) return 'VERDE';
    if (
      p.estadoMandatos === 'ROJO' ||
      // === false y no !cumple4PorMil: `null` significa que el 4‰ no le corresponde
      // (alianza o confederación), y eso no es un incumplimiento.
      p.cumple4PorMil === false ||
      p.paridad === 'NO_CUMPLE'
    ) return 'ROJO';
    if (p.estadoMandatos === 'AMARILLO') return 'AMARILLO';
    return 'VERDE';
  };

  // Filtrado client-side para cumplimiento
  const cumplimientoData = useMemo(() => {
    if (!cumplimientoRaw) return [];
    const result = cumplimientoRaw.filter(p => {
      // Se busca contra todo lo que identifica al partido, no solo la
      // denominación: número, sigla y localidad. Ignora acentos y mayúsculas y
      // acepta palabras sueltas en cualquier orden ("victoria madryn").
      if (search && !coincideBusqueda(search, `${p.numero} ${p.denominacion} ${p.sigla ?? ''} ${p.localidad ?? ''}`)) {
        return false;
      }
      if (estadoFilter && p.estado !== estadoFilter) return false;
      if (tipoFilter && p.tipo !== tipoFilter) return false;
      if (claseFilter && p.clase !== claseFilter) return false;
      if (soloAtencion) {
        // Los inactivos nunca entran en "solo atención": no hay nada que reclamarles.
        if (esInactivo(p)) return false;
        const eg = calcularEstadoGeneral(p);
        if (eg === 'VERDE') return false;
      }
      return true;
    });
    // El orden natural viene del backend (tipo, luego denominación). Al pedir
    // orden por Nro se ordena solo por número, ignorando el tipo; al apagarlo se
    // vuelve al original — por eso se copia el array en vez de ordenarlo in situ.
    return ordenPorNro ? [...result].sort((a, b) => a.numero - b.numero) : result;
  }, [cumplimientoRaw, search, estadoFilter, tipoFilter, claseFilter, soloAtencion, ordenPorNro]);

  // Descarga del listado en PDF. Se mandan las filas YA filtradas y ordenadas:
  // el PDF tiene que decir exactamente lo que hay en pantalla, y las etiquetas
  // de etapa se resuelven acá (dependen de las fases del reconocimiento).
  const handleDescargarPdf = async () => {
    if (exportandoPdf) return;
    setExportandoPdf(true);
    try {
      await partidoService.descargarListadoPdf({
        filas: cumplimientoData.map(p => {
          const t = etapaTramite(p);
          return {
            numero: p.numero,
            denominacion: p.denominacion,
            sigla: p.sigla,
            tipo: tipoLabels[p.tipo] || p.tipo,
            // Solo se rotula lo que no es un partido común: en el PDF la clase va
            // pegada a la sigla, y "Partido" en cada renglón sería puro ruido.
            clase: p.clase === 'ESTANDAR' ? null : (claseLabels[p.clase] || p.clase),
            localidad: p.localidad,
            expedienteTipo: t?.tramite ?? null,
            expedienteNumero: p.expediente?.numero ?? null,
            estado: estadoLabels[p.estado] || p.estado,
            etapa: t?.etapa ?? 'Sin trámite',
            cumple4PorMil: p.cumple4PorMil,
            nota: p.nota,
          };
        }),
        filtros: {
          busqueda: search || null,
          estado: estadoFilter ? (estadoLabels[estadoFilter] || estadoFilter) : null,
          tipo: tipoFilter ? (tipoLabels[tipoFilter] || tipoFilter) : null,
          clase: claseFilter ? (claseLabels[claseFilter] || claseFilter) : null,
        },
      });
      toast.success('Listado descargado');
    } catch (err: any) {
      let mensaje = 'No se pudo generar el PDF del listado';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          mensaje = parsed?.error?.message || parsed?.message || mensaje;
        } catch { /* respuesta no-JSON */ }
      }
      toast.error(mensaje);
    } finally {
      setExportandoPdf(false);
    }
  };

  // Definición de columnas (vista estándar — no usada en isCumplimiento)
  const columns: Column<Partido>[] = [
    {
      key: 'denominacion',
      header: 'Denominación',
      sortable: true,
      render: (value, row) => (
        <Link to={`/partidos/${row.id}`} className="group">
          <p className="font-medium text-blue-600 group-hover:text-blue-800">{value}</p>
          <p className="text-sm text-gray-500">{row.sigla}</p>
        </Link>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      width: '120px',
      render: (value) => tipoLabels[value] || value,
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '130px',
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estadoColors[value] || 'bg-gray-100 text-gray-800'}`}>
          {estadoLabels[value] || value}
        </span>
      ),
    },
    {
      key: '_count.afiliaciones',
      header: 'Afiliados',
      width: '100px',
      render: (_, row) => (
        <span className="text-gray-600">
          {row._count?.afiliaciones?.toLocaleString() || '-'}
        </span>
      ),
    },
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setEstadoFilter('');
    setTipoFilter('');
    setClaseFilter('');
    setPage(1);
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error al cargar los partidos</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partidos Políticos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Registro de partidos políticos, confederaciones y alianzas del distrito
          </p>
        </div>
        {isAdmin && (
          <Link
            to="/partidos/nuevo"
            className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            <PlusIcon className="h-5 w-5" />
            Nuevo Partido
          </Link>
        )}
      </div>

      {/* Barra de búsqueda y filtros */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Búsqueda */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  // La tabla de cumplimiento filtra en memoria, así que puede
                  // responder a cada tecla. La vista estándar pagina contra el
                  // servidor y sigue esperando el Enter.
                  if (isCumplimiento) { setSearch(e.target.value); setPage(1); }
                }}
                placeholder="Buscar por número, denominación, sigla o localidad..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* --- OCULTO (reutilizable): Toggle "Solo requieren atención" ---
          {isCumplimiento && (
            <button
              onClick={() => setSoloAtencion(!soloAtencion)}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition text-sm font-medium ${
                soloAtencion
                  ? 'border-red-500 text-red-600 bg-red-50'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${soloAtencion ? 'bg-red-500' : 'bg-gray-400'}`} />
              Solo requieren atención
            </button>
          )}
          --- fin toggle --- */}

          {/* --- OCULTO (reutilizable): Botón de filtros (los filtros ahora quedan fijos) ---
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition ${
              showFilters || estadoFilter || tipoFilter
                ? 'border-primary-500 text-primary-600 bg-primary-50'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            Filtros
            {(estadoFilter || tipoFilter) && (
              <span className="bg-primary-600 text-white text-xs rounded-full px-2 py-0.5">
                {[estadoFilter, tipoFilter].filter(Boolean).length}
              </span>
            )}
          </button>
          --- fin botón filtros --- */}
        </div>

        {/* Panel de filtros (fijo: Estado y Tipo siempre visibles) */}
        {(
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={estadoFilter}
                onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos los estados</option>
                {Object.entries(estadoLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <select
                value={tipoFilter}
                onChange={(e) => { setTipoFilter(e.target.value); setPage(1); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos los tipos</option>
                {Object.entries(tipoLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clase
              </label>
              <select
                value={claseFilter}
                onChange={(e) => { setClaseFilter(e.target.value); setPage(1); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todas las clases</option>
                <option value="ESTANDAR">Solo partidos</option>
                <option value="CONFEDERACION">Solo confederaciones</option>
                <option value="ALIANZA">Solo alianzas</option>
              </select>
            </div>

            <div className="flex items-end justify-between gap-3">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Limpiar filtros
              </button>
              {isCumplimiento && (
                <button
                  onClick={handleDescargarPdf}
                  disabled={exportandoPdf || cumplimientoLoading}
                  title="Descargar el listado en PDF, con los filtros aplicados"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {exportandoPdf ? (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <DocumentTextIcon className="h-5 w-5" />
                  )}
                  Descargar PDF
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* --- OCULTO (reutilizable): contador de seleccionados (depende del selector) ---
      {isCumplimiento && selectedIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <span className="font-medium">{selectedIds.size} {selectedIds.size === 1 ? 'partido seleccionado' : 'partidos seleccionados'}</span>
          <button onClick={() => setSelectedIds(new Set())} className="text-blue-500 hover:text-blue-700 underline">Deseleccionar todos</button>
        </div>
      )}
      --- fin contador --- */}

      {/* Tabla */}
      {isCumplimiento ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {cumplimientoLoading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : cumplimientoData.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No se encontraron agrupaciones</div>
          ) : (
            <div className="overflow-x-auto">
              {/* Anchos: todas las columnas se encogen a su contenido (w-px en
                  layout auto) salvo "Nota", que se queda con el resto. Es la
                  columna que más se lee y la única con texto libre. */}
              <table className="w-full text-xs table-auto">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {/* --- OCULTO (reutilizable): columna selector ---
                    <th className="px-2 py-2.5 w-10"><input type="checkbox" checked={selectedIds.size === cumplimientoData.length && cumplimientoData.length > 0} onChange={e => setSelectedIds(e.target.checked ? new Set(cumplimientoData.map(p => p.id)) : new Set())} className="rounded border-gray-300" /></th>
                    --- fin selector --- */}
                    <th className="px-1.5 py-2 text-right text-[11px] font-medium text-gray-500 uppercase w-px whitespace-nowrap">
                      <button
                        onClick={() => setOrdenPorNro(v => !v)}
                        title={ordenPorNro ? 'Volver al orden por tipo de partido' : 'Ordenar por número, ascendente'}
                        className={`inline-flex items-center gap-1 uppercase hover:text-gray-700 ${ordenPorNro ? 'text-primary-600 font-semibold' : ''}`}
                      >
                        Nro
                        <span className="text-[10px]" aria-hidden="true">{ordenPorNro ? '▲' : '⇅'}</span>
                      </button>
                    </th>
                    <th className="px-1.5 py-2 text-left text-[11px] font-medium text-gray-500 uppercase w-px whitespace-nowrap">Denominación</th>
                    <th className="px-1.5 py-2 text-left text-[11px] font-medium text-gray-500 uppercase w-px whitespace-nowrap">Tipo</th>
                    <th className="px-1.5 py-2 text-left text-[11px] font-medium text-gray-500 uppercase w-[92px]">Localidad</th>
                    <th className="px-1.5 py-2 text-left text-[11px] font-medium text-gray-500 uppercase w-px whitespace-nowrap">Exp. Inicial</th>
                    <th className="px-1.5 py-2 text-left text-[11px] font-medium text-gray-500 uppercase w-px whitespace-nowrap">Estado</th>
                    <th className="px-1.5 py-2 text-left text-[11px] font-medium text-gray-500 uppercase w-px whitespace-nowrap">Etapa del trámite</th>
                    <th className="px-1.5 py-2 text-center text-[11px] font-medium text-gray-500 uppercase w-px whitespace-nowrap">Cumple 4‰</th>
                    {/* --- OCULTO (reutilizable): columna Última carga ---
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Última carga</th>
                    --- fin columna oculta --- */}
                    <th className="px-1 py-2 text-center text-[11px] font-medium text-gray-500 uppercase w-px whitespace-nowrap" title="Hoja de inspección (.docx)">Hoja</th>
                    <th className="px-1.5 py-2 text-left text-[11px] font-medium text-gray-500 uppercase min-w-[280px]">Nota</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cumplimientoData.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                    {/* --- OCULTO (reutilizable): celda selector ---
                    <td className="px-1.5 py-2"><input type="checkbox" checked={selectedIds.has(p.id)} onChange={e => setSelectedIds(prev => { const next = new Set(prev); e.target.checked ? next.add(p.id) : next.delete(p.id); return next; })} className="rounded border-gray-300" /></td>
                    --- fin celda selector --- */}
                      <td className="px-1.5 py-2 text-right text-gray-600 tabular-nums whitespace-nowrap ">{p.numero}</td>
                      <td className="px-1.5 py-2">
                        {/* El nombre lleva al EXPEDIENTE INICIAL (el mismo destino que la
                            columna "Exp. Inicial"): es el trámite lo que se viene a mirar,
                            no la ficha del partido. Sin trámite no hay expediente al que
                            ir, así que ahí sí queda la ficha — si no, el nombre dejaría de
                            ser clickeable y se perdería la única entrada. */}
                        <Link
                          to={p.expediente ? `/expedientes/${p.expediente.id}` : `/partidos/${p.id}`}
                          className="group"
                          title={p.expediente ? `Ver expediente ${p.expediente.numero}` : 'Ver ficha del partido (sin trámite)'}
                        >
                          <p className="font-medium text-blue-600 group-hover:text-blue-800 leading-tight text-sm">{p.denominacion}</p>
                          {/* La clase solo se rotula cuando NO es un partido común: es
                              lo que avisa de un vistazo que a esa fila no se le pide
                              padrón propio ni hoja de inspección. */}
                          <p className="text-[11px] text-gray-500 leading-tight flex items-center gap-1.5 flex-wrap">
                            <span>{p.sigla}</span>
                            {p.clase !== 'ESTANDAR' && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${claseBadgeColors[p.clase] || 'bg-gray-100 text-gray-700'}`}>
                                {claseLabels[p.clase] || p.clase}
                              </span>
                            )}
                          </p>
                        </Link>
                      </td>
                      <td className="px-1.5 py-2 text-gray-600 whitespace-nowrap text-sm">{tipoLabels[p.tipo] || p.tipo}</td>
                      <td className="px-1.5 py-2 text-gray-600 text-sm leading-tight break-words">{p.localidad || <span className="text-gray-300">—</span>}</td>
                      {/* Exp. Inicial: por cuál de los dos trámites entró el partido al
                          sistema. El número va abajo y del MISMO cuerpo que el tipo,
                          para poder identificar el expediente de un vistazo. */}
                      <td className="px-1.5 py-2">
                        {p.expediente ? (
                          <Link to={`/expedientes/${p.expediente.id}`} className="group block whitespace-nowrap" title={`Ver expediente ${p.expediente.numero}`}>
                            <p className="text-xs text-gray-700 group-hover:text-blue-600 leading-tight">{etapaTramite(p)?.tramite}</p>
                            <p className="text-xs text-gray-500 group-hover:text-blue-600 leading-tight">{p.expediente.numero}</p>
                          </Link>
                        ) : (
                          <span className="text-xs text-gray-400">Sin trámite</span>
                        )}
                      </td>
                      <td className="px-1.5 py-2">
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium ${estadoColors[p.estado] || 'bg-gray-100 text-gray-800'}`}>
                            {estadoLabels[p.estado] || p.estado}
                          </span>
                          {/* Apunta a la etapa del trámite de la columna siguiente. Solo
                              si hay trámite: sin él la flecha no llevaría a ninguna parte. */}
                          {p.expediente && (
                            <span className="text-gray-400 font-mono text-xs" aria-hidden="true">--&gt;</span>
                          )}
                        </div>
                      </td>
                      <td className="px-1.5 py-2">
                        {(() => {
                          const t = etapaTramite(p);
                          if (!t) return <span className="text-xs text-gray-400">Sin trámite</span>;
                          // Sin subtítulo: de qué trámite se trata ya lo dice
                          // "Exp. Inicial", acá solo interesa en qué etapa está.
                          return (
                            <Link to={`/expedientes/${p.expediente!.id}`} className="group inline-block" title={`Ver expediente ${p.expediente!.numero}`}>
                              <p className="text-xs text-gray-700 group-hover:text-blue-600 whitespace-nowrap">{t.etapa}</p>
                            </Link>
                          );
                        })()}
                      </td>
                      <td className="px-1.5 py-2 text-center">
                        {/* Sin padrón propio no hay 4‰ que evaluar, y tampoco hay
                            adónde ir: el link a /afiliados no tendría nada que mostrar. */}
                        {p.cumple4PorMil == null ? (
                          <span
                            className="text-gray-300"
                            title={`No corresponde: ${(claseLabels[p.clase] || p.clase).toLowerCase()} sin padrón propio`}
                          >
                            —
                          </span>
                        ) : (
                          <Link
                            to={`/afiliados?partido=${p.id}`}
                            title="Ver afiliados del partido"
                            className="inline-flex items-center gap-1.5 hover:opacity-80 whitespace-nowrap"
                          >
                            <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-medium ${
                              p.cumple4PorMil ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {p.cumple4PorMil ? 'Cumple' : 'No cumple'}
                            </span>
                            <span className="text-blue-500 text-xs font-bold underline underline-offset-1" aria-hidden="true">↗</span>
                          </Link>
                        )}
                      </td>
                      {/* --- OCULTO (reutilizable): celda Última carga ---
                      <td className="px-2 py-2.5 text-xs text-gray-500">
                        {p.ultimaActualizacion
                          ? new Date(p.ultimaActualizacion).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '—'}
                      </td>
                      --- fin celda oculta --- */}
                      <td className="px-1 py-2 text-center">
                        {/* La hoja evalúa el REEMPADRONAMIENTO y una alianza no
                            reempadrona: se constituye para una elección y muere con
                            ella. La confederación sí la tiene (sección H, art. 8). El
                            backend rechaza igual la alianza; esto solo evita ofrecer
                            un botón que no puede funcionar. */}
                        {p.clase === 'ALIANZA' ? (
                          <span className="text-gray-300" title="Las alianzas no tienen hoja de inspección: no reempadronan">—</span>
                        ) : (
                        <button
                          onClick={() => handleExportarHoja(p)}
                          disabled={exportandoId === p.id}
                          title="Exportar hoja de inspección (.docx)"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {exportandoId === p.id ? (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : (
                            <DocumentArrowDownIcon className="h-5 w-5" />
                          )}
                        </button>
                        )}
                      </td>
                      <CeldaNota partido={p} puedeAnotar={puedeAnotar} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <DataTable
          data={data?.data || []}
          columns={columns}
          loading={isLoading}
          emptyMessage="No se encontraron partidos con los filtros aplicados"
          onRowClick={(row) => navigate(`/partidos/${row.id}`)}
          pagination={{
            page,
            pageSize,
            total: data?.meta?.totalItems || 0,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
        />
      )}

      {/* Estadísticas rápidas */}
      {data?.meta && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Total Partidos</p>
            <p className="text-2xl font-bold text-gray-900">{data.meta.totalItems}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Vigentes</p>
            <p className="text-2xl font-bold text-green-600">
              {data.data.filter((p: Partido) => p.estado === 'VIGENTE').length}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">En Formación</p>
            <p className="text-2xl font-bold text-yellow-600">
              {data.data.filter((p: Partido) => p.estado === 'EN_FORMACION').length}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow">
            <p className="text-sm text-gray-500">Suspendidos</p>
            <p className="text-2xl font-bold text-orange-600">
              {data.data.filter((p: Partido) => p.estado === 'SUSPENDIDO').length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
